import { ExternalLink, FileDown } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { useToast } from '@/components/ui/toast';
import { usePermission } from '@/hooks/usePermission';
import { apiErrorReason } from '@/lib/api';

import { TakeLmsVersion } from './TakeLmsVersion';
import {
  LMS_DIVERGED_QUALITY_TEXT,
  isViewUnavailable,
  onlineEditErrorText,
  onlineViewIssueText,
} from '../online-errors';
import type { OnlineView } from '../types';
import { useOnlineCapture, useOnlineEdit, useOnlineView } from '../use-online-editing';

interface Props {
  entryId: string;
  code: string;
  companyId: string | null;
  /** El flujo permite editar el binario en esta etapa (borrador/en_revision). */
  canEdit: boolean;
}

const POPUP_BLOCKED =
  'El navegador bloqueó la pestaña de Office. Permite las ventanas emergentes de este sitio y vuelve a presionar Editar en Office.';

/** Rótulo de lo que se ve: el borrador del ciclo o la versión vigente. */
function KindBadge({ view }: { view: OnlineView }): ReactElement {
  return view.kind === 'working' ? (
    <span className="badge b-warn">Borrador en edición · v{view.version}</span>
  ) : (
    <span className="badge b-ok">Versión vigente · v{view.version}</span>
  );
}

/** Edición en línea (Opción A): vista incrustada de solo lectura + edición en pop-out
 *  de Office (el editor no se incrusta por CSP) + captura de la versión al volver. */
export function OnlineEditorPanel({ entryId, code, companyId, canEdit }: Props): ReactElement {
  const view = useOnlineView(entryId, { companyId, enabled: companyId !== null });
  const edit = useOnlineEdit();
  const capture = useOnlineCapture();
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);
  // Calidad puede tomar la versión cargada en el gestor documental fuera del QMS.
  const canTakeLms = usePermission('quality:document:transition');
  const [diverged, setDiverged] = useState(false);

  const openEditor = (): void => {
    edit.mutate(
      { entryId },
      {
        onSuccess: (data) => {
          // Sin 'noopener' en las opciones (con él window.open siempre devuelve null y no se
          // distingue un bloqueo); el vínculo con esta pestaña se corta a mano.
          const tab = window.open(data.editUrl, '_blank');
          if (tab === null) {
            showToast(POPUP_BLOCKED, 'error');
            return;
          }
          tab.opener = null;
          setEditing(true);
          showToast('Editor de Office abierto en otra pestaña');
        },
        onError: (err) => {
          const lmsDiverged = apiErrorReason(err) === 'lms_version_diverged';
          setDiverged(lmsDiverged);
          showToast(
            lmsDiverged && canTakeLms
              ? LMS_DIVERGED_QUALITY_TEXT
              : onlineEditErrorText(err, 'No se pudo abrir el editor'),
            'error',
          );
        },
      },
    );
  };

  const captureVersion = (): void => {
    capture.mutate(
      { entryId },
      {
        onSuccess: (data) => {
          setEditing(false);
          showToast(`Versión ${data.versionLabel} capturada`);
        },
        onError: (err) =>
          showToast(onlineEditErrorText(err, 'No se pudo capturar la versión'), 'error'),
      },
    );
  };

  const noBinary = apiErrorReason(view.error) === 'no_binary';

  return (
    <div className="editorwrap">
      <div className="etb">
        <strong style={{ fontSize: 12.5 }}>Vista del documento</strong>
        {view.data ? <KindBadge view={view.data} /> : null}
        {canEdit ? (
          <>
            <button
              type="button"
              className="btn sm"
              onClick={openEditor}
              disabled={edit.isPending || noBinary}
            >
              <ExternalLink size={14} aria-hidden="true" />
              Editar en Office
            </button>
            {editing ? (
              <button
                type="button"
                className="btn mut sm"
                onClick={captureVersion}
                disabled={capture.isPending}
              >
                <FileDown size={14} aria-hidden="true" />
                {capture.isPending ? 'Capturando…' : 'Capturar versión'}
              </button>
            ) : null}
            {diverged && canTakeLms ? (
              <TakeLmsVersion entryId={entryId} onTaken={() => setDiverged(false)} />
            ) : null}
          </>
        ) : null}
        <span className="fname" title={`${code}`}>
          {code}
        </span>
      </div>
      {view.isLoading ? (
        <div className="noembed">Cargando la vista del documento…</div>
      ) : view.isError ? (
        <div className="noembed" role={isViewUnavailable(view.error) ? 'status' : 'alert'}>
          {onlineViewIssueText(view.error)}
        </div>
      ) : view.data ? (
        <iframe className="frame" src={view.data.embedUrl} title={`Vista de ${code}`} />
      ) : null}
    </div>
  );
}
